import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useEstablishments } from "@/context/EstablishmentContext";
import api from "@/lib/api";
import { Receipt, FileText, Package, Truck, BookOpen, Bell, TrendingUp, FileSignature, ClipboardCheck } from "lucide-react";
import { PurchasesDeliveries } from "@/components/purchases/PurchasesDeliveries";
import { PurchasesOverview } from "@/components/purchases/PurchasesOverview";
import { PurchasesInvoices } from "@/components/purchases/PurchasesInvoices";
import { PurchasesItems } from "@/components/purchases/PurchasesItems";
import { PurchasesSuppliers } from "@/components/purchases/PurchasesSuppliers";
import { PurchasesCompositions } from "@/components/purchases/PurchasesCompositions";
import { PurchasesAlerts } from "@/components/purchases/PurchasesAlerts";
import { PurchasesMargins } from "@/components/purchases/PurchasesMargins";

export default function Purchases() {
  const { current } = useEstablishments();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [ent, setEnt] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => { if (current?.id) api.get(`/purchases/${current.id}/entitlements`).then((r) => setEnt(r.data)).catch(() => {}); }, [current?.id, refresh]);
  if (!current) return <p className="text-sm text-slate-500 p-4">Sélectionnez un établissement.</p>;
  const v = ent?.vocab || {};
  const TABS = [
    { id: "overview", label: "Vue d'ensemble", icon: Receipt }, { id: "invoices", label: "Factures", icon: FileText }, { id: "quotes", label: "Devis", icon: FileSignature }, { id: "deliveries", label: "Bons de livraison", icon: ClipboardCheck }, { id: "items", label: "Articles", icon: Package },
    { id: "suppliers", label: "Fournisseurs", icon: Truck }, { id: "compositions", label: v.compositions || "Compositions", icon: BookOpen }, { id: "margins", label: "Mes marges", icon: TrendingUp }, { id: "alerts", label: "Alertes", icon: Bell },
  ];
  const go = (id, item, scan) => setParams(id === "overview" ? {} : item ? { tab: id, item } : scan ? { tab: id, scan } : { tab: id });
  const common = { est: current, ent, vocab: v, go, onChange: () => setRefresh((x) => x + 1) };
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-4" data-testid="purchases-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2"><Receipt className="w-6 h-6 text-blue-600" /> Achats & Marges</h1>
        <p className="text-sm text-slate-500 mt-1">Importez vos factures. Respondia surveille vos prix d'achat et leur impact sur votre rentabilité.</p>
      </div>
      <nav className="flex gap-1 overflow-x-auto max-w-full pb-1" data-testid="purchases-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => go(t.id)} data-testid={`purchases-tab-${t.id}`} className={`shrink-0 inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl border transition-colors ${tab === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </nav>
      {ent && !ent.vocab?.trade_known && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="purchases-trade-unknown">
          Métier précis non renseigné pour cet établissement : Respondia utilise un vocabulaire générique (compositions, coût direct). Précisez votre métier (restaurant, fleuriste, coiffeur, garage…) dans <a href="/app/establishments" className="underline font-medium">la fiche de l'établissement</a> pour adapter les intitulés.
        </div>
      )}
      {tab === "overview" && <PurchasesOverview key={refresh} {...common} />}
      {tab === "invoices" && <PurchasesInvoices {...common} initialScan={params.get("scan")} />}
      {tab === "quotes" && <PurchasesInvoices key="quotes" {...common} docType="quote" />}
      {tab === "deliveries" && <PurchasesDeliveries {...common} />}
      {tab === "items" && <PurchasesItems {...common} itemId={params.get("item")} />}
      {tab === "suppliers" && <PurchasesSuppliers {...common} supplierId={params.get("supplier")} />}
      {tab === "compositions" && <PurchasesCompositions {...common} />}
      {tab === "margins" && <PurchasesMargins {...common} />}
      {tab === "alerts" && <PurchasesAlerts {...common} />}
    </div>
  );
}
